import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Box, Center, Flex } from "@mantine/core";
import { Dropzone, FileWithPath, IMAGE_MIME_TYPE } from "@mantine/dropzone";
import { TFunction } from "i18next";
import { useSelector } from "react-redux";

import { Select } from 'components';
import { AppConstants, ServiceConstants } from "constant";
import { UserService } from "services";
import { Language, UserResponse } from "types";
import { AppUtils, StorageUtils } from "utils";

import { IRootState } from "config/redux";

import Button from '../button/standard';
import Modal from './base';
import TextInput from '../input/standard';
import Text from '../text';
import Title from '../title';

const { STORAGE_USER } = AppConstants;
const { REQUEST_PENDING, REQUEST_USERS_CLIENTS_UPDATE_PROPERTY_ID, REQUEST_USER_CHANGE_AVATAR } = ServiceConstants;

interface MyProfileInfoProps {
  opened: boolean;
  onClose: (val: boolean) => void;
  t: TFunction;
  user: UserResponse;
}
const MyProfileInfo = ({ opened, onClose, t, user }: MyProfileInfoProps) => {

  const serviceId = REQUEST_USERS_CLIENTS_UPDATE_PROPERTY_ID + user.id + 'language';
  const updatePropertyServiceObject = useSelector((state: IRootState) => state.service.object[serviceId]);
  const updateAvatarServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_USER_CHANGE_AVATAR]);

  const [selectedLanguage, setSelectedLanguage] = useState<Language>(user.language);
  const [selectedFiles, setSelectedFiles] = useState<FileWithPath[]>([]);
  const requestSent = useRef<number>(0);

  useEffect(() => {
    if (!opened) {
      setSelectedLanguage(user.language);
      setSelectedFiles([]);
    }
  }, [opened]);

  useEffect(() => {
    AppUtils.resolveServiceObject(updatePropertyServiceObject, serviceId, (val) => {
      const storageUser = StorageUtils.getObject(STORAGE_USER);
      StorageUtils.setObject(STORAGE_USER, { ...storageUser, language: val.value })
      AppUtils.changeLanguage(val.value);
      requestSent.current--;
      if (requestSent.current === 0) {
        window.location.reload();
      }
    })
    AppUtils.resolveServiceObject(updateAvatarServiceObject, serviceId, (val) => {
      const storageUser = StorageUtils.getObject(STORAGE_USER);
      StorageUtils.setObject(STORAGE_USER, { ...storageUser, profileImage: val });
      requestSent.current--;
      if (requestSent.current === 0) {
        window.location.reload();
      }
    })
  }, [updatePropertyServiceObject, updateAvatarServiceObject]);

  const isLoading = useMemo(() =>
    (updatePropertyServiceObject && updatePropertyServiceObject.status === REQUEST_PENDING)
    || (updateAvatarServiceObject && updateAvatarServiceObject.status === REQUEST_PENDING),
  [updatePropertyServiceObject, updateAvatarServiceObject]);

  const renderAvatar = useMemo(() => {
    if (selectedFiles.length > 0) {
      const el = selectedFiles[0];
      const imageUrl = URL.createObjectURL(el);
      return <Avatar size={60} src={imageUrl} />
    }
    if (user.profileImage) {
      const imageUrl = `${process.env.REACT_APP_API_URL_ATTACHMENTS}/${user.profileImage}`;
      return <Avatar size={60} src={imageUrl} />
    }
    return <Avatar size={60} />;
  }, [user, selectedFiles]);

  const isButtonDisabled = useMemo(() => {
    let disabled = true;
    if (selectedLanguage && selectedLanguage !== user.language) {
      disabled = false;
    }
    if (selectedFiles.length > 0) {
      disabled = false;
    }
    return disabled;
  }, [selectedLanguage, selectedFiles]);

  const saveProfileInfo = useCallback(() => {
    requestSent.current = 1;
    if ((selectedLanguage && selectedLanguage !== user.language) && selectedFiles.length > 0) {
      requestSent.current = 2;
    }
    if (selectedLanguage && selectedLanguage !== user.language) {
      UserService.userClientUpdateProperty(user.id, 'language', selectedLanguage);
    }
    if (selectedFiles.length > 0) {
      UserService.changeAvatar(selectedFiles[0]);
    }
  }, [selectedLanguage, selectedFiles]);

  return (
    <Modal opened={opened} onClose={() => onClose(false)} title={t('profile')}>
      <Flex mb={'sm'}>
        <Dropzone
          accept={IMAGE_MIME_TYPE}
          onDrop={setSelectedFiles}
          multiple={false}
          w={78}
          h={78}
          style={{ borderRadius: 100, padding: 8 }}
        >
          {renderAvatar}
        </Dropzone>
        <Box>
          <Title ml={'sm'} mb={0} order={4}>{user.fullName}</Title>
          <Text ml={'sm'}>{t('user.profile.' + user.profile.code)}</Text>
        </Box>
      </Flex>
      <TextInput
        label={t('user.phone.label')}
        defaultValue={user.countryCode && user.phoneNumber ? (user.countryCode + ' ' + user.phoneNumber) : undefined}
        disabled={true}
        mb={'sm'}
      />
      <TextInput
        label={t('user.email.label')}
        defaultValue={user.email}
        disabled={true}
        mb={'sm'}
      />
      <Select
        label={t('user.language.label')}
        defaultValue={user.language}
        data={[
          { value: 'DE', label: t(`user.language.options.DE`)},
          { value: 'EN', label: t(`user.language.options.EN`)},
          { value: 'SR', label: t(`user.language.options.SR`)}
        ]}
        allowDeselect={false}
        onChange={(val) => setSelectedLanguage(val as Language)}
        disabled={isLoading}
        mb={'sm'}
      />
      <Center mt={'xl'}>
        <Button
          disabled={isButtonDisabled}
          loading={isLoading}
          onClick={saveProfileInfo}>
          {t('action.submit')}
        </Button>
      </Center>
    </Modal>
  )
}

export default MyProfileInfo;